import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useApi } from 'services/api';
import type { IdBookmark, PqlBookmark, BookmarkType } from './types';

type EditFormValues = {
  name: string;
  description?: string;
  labels?: string;
  query?: string;
  view?: string;
};

export type EditBookmarkParams = {
  values: EditFormValues;
  bookmark: IdBookmark | PqlBookmark;
  type: BookmarkType;
};

const getPayload = ({ bookmark, type, values }: EditBookmarkParams) => {
  if (type === 'id') {
    const idBookmark = bookmark as IdBookmark;
    return {
      name: values.name,
      description: values.description || '',
      labels: values.labels && typeof values.labels === 'string' ? values.labels.split(',').map((l) => l.trim()) : [],
      objectId: idBookmark.objectId,
      searchType: idBookmark.searchType,
    };
  }

  return {
    name: values.name,
    description: values.description || '',
    labels: values.labels && typeof values.labels === 'string' ? values.labels.split(',').map((l) => l.trim()) : [],
    query: values.query,
    searchType: bookmark.searchType,
    view: values.view || '',
  };
};

const useEditBookmark = () => {
  const api = useApi();
  const client = useQueryClient();

  return useMutation({
    mutationFn: async ({ values, bookmark, type }: EditBookmarkParams) => {
      const params = getPayload({ bookmark, type, values });

      const editedBookmark = await api.post(`/bookmarks/${type}/${bookmark.id}`, params);

      const records = client.getQueryData<(IdBookmark | PqlBookmark)[]>(['bookmarks', type]) ?? [];

      client.setQueryData(
        ['bookmarks', type],
        records.map((b) => (b.id === bookmark.id ? editedBookmark : b)),
      );
    },
  });
};

export { useEditBookmark };
